/**
 * OFFICIAL TAXONOMY V3 — single source of truth for the (tipo → categoría → subcategoría)
 * hierarchy. Extracted from geminiService.ts so that classification-only consumers
 * (legacyContextMigration, importer, forms) can derive `tipo` from a category WITHOUT
 * pulling the whole Gemini SDK into their import graph. geminiService re-exports every
 * symbol here, so existing imports keep working.
 */
import type { TipoMovimiento } from '../types';

// Structure segmented by bucket (GASTO | INGRESO | INVERSION | TRASPASO).
// Changes vs V2:
//  - New Tipo 'Traspaso' (neutral, doesn't affect P&L): Movimiento Interno / Externo.
//  - INGRESO: Devoluciones + Compensaciones → unified into 'Ajustes'. New cat 'Venta'.
//    Retribuciones now includes 'Donación'. Aportación Cuenta Común migrates to Traspaso.
//  - GASTO: new category 'Préstamo' (installments / money lent to third parties).
//  - INVERSION: 'Inversión' (single cat) → 4 contexts × multiple categories:
//    Aportación, Retiro, Ganancias (Dividendos/Alquiler Cobrado), Pérdidas (asset expenses).
//  - 'Préstamo' in the Ingreso context → bucket INGRESO; in Inversión → bucket INVERSION.
//    getBucketFromCategory accepts opts { tipo, contexto } to disambiguate.

// === GASTOS (bucket=GASTO) ===
export const EXPENSE_TAXONOMY: { [key: string]: string[] } = {
    "Compra":      ["Supermercado", "Ropa", "Hogar", "Regalos", "Electrónica", "Vehículos", "Deporte", "Seguros", "Impuestos", "Online", "C - Otros"],
    "Personales":  ["Telefonía", "Suscripción Diario", "Seguros", "Impuestos", "P - Otros"],
    "Formación":   ["Colegio", "Material", "Libros", "Excursiones", "Cursos", "Estudios", "Extraescolares", "Seguros", "Impuestos", "F - Otros"],
    "Ocio":        ["Vacaciones", "Paseos", "Espectáculos", "Deporte", "Restaurantes", "Bares", "Juegos", "Música", "Fiesta", "Celebración", "Televisión", "Seguros", "Impuestos", "O - Otros"],
    "Transporte":  ["Taller", "Combustible", "Multas", "Garage", "Taxi", "Peaje", "Parking", "Transporte Público", "Carsharing", "Alquiler de vehículo", "Seguros", "Impuestos", "T - Otros"],
    "Vivienda":    ["Agua", "Luz", "Gas", "Reparaciones", "Alquiler Vivienda", "Hipoteca", "Jardinero", "Limpieza", "Internet", "Seguros", "Impuestos", "V - Otros"],
    "Salud":       ["Médicos", "Farmacia", "Cuidado Personal", "Obra Social", "Gimnasio", "Nutrición", "Seguros", "Impuestos", "S - Otros"],
    "Mascotas":    ["Alimentos", "Veterinario", "Juguetes", "Paseador", "Farmacia", "Bienestar", "Seguros", "Impuestos", "M - Otros"],
    "Trabajo":     ["Alimentación", "Desplazamiento", "Indumentaria", "Compromisos", "Tbj - Otros"],
    "Préstamo":    ["Préstamo personal", "Hipoteca", "Pr - Otros"],
};

// === INGRESOS (bucket=INGRESO) ===
export const INCOME_TAXONOMY: { [key: string]: string[] } = {
    "Retribuciones":  ["Nómina", "Pensión", "Extra", "Objetivos", "Subvención", "Premio", "R - Otros"],
    // Sale of personal belongings.
    "Venta":          ["Ropa", "Mobiliario", "Electrónica", "Vehículo", "Vta - Otros"],
    // Unifies the old Devoluciones + Compensaciones (except Aportación Cuenta Común → Traspaso).
    "Ajustes":        ["Reembolso Seguro", "Reembolso Mutua", "Devolución Hacienda", "Devolución", "Venta Particular", "Bizum Devolución", "Bizum Recibido", "Pago Erróneo Devuelto", "Tricount", "A - Otros"],
    "Donación":       ["Familiar", "Amigos", "Entidad", "Don - Otros"],
    "Herencia":       ["Efectivo", "Inmueble", "Bienes Muebles", "Cartera", "Her - Otros"],
    // Money received as a loan (generates positive debt). Bucket INGRESO by default;
    // when tipo='Inversión' the bucket resolves to INVERSION via getBucketFromCategory opts.
    "Préstamo":       ["Préstamo personal", "Hipoteca", "Pr - Otros"],
};

// === INVESTMENT (bucket=INVERSION; requires assetId for assets) ===
// Each category corresponds to a specific context of the 'Inversión' tipo.
export const INVESTMENT_TAXONOMY: { [key: string]: string[] } = {
    // 'Aportación' context: capital going into or being reinvested in the asset.
    "Adquisición": ["Inmueble", "Mobiliario", "Materiales", "Equipo", "Empresa", "Adq - Otros"],
    "Reinversión":  ["Mejora", "Mantenimiento", "Aumento de liquidez", "Rei - Otros"],
    "Fondos":       ["Acciones", "Fondos de Inversión", "Jubilación", "Materias Primas", "Fondos - Otros"],
    // 'Retiro' context: recovery of capital from the asset.
    "Retiro":           ["Reembolso", "Venta Activo", "Rt - Otros"],
    // 'Ganancias' context: returns generated by the asset.
    "Dividendos":       ["Ordinario", "Extraordinario", "Di - Otros"],
    "Alquiler Cobrado": ["Largo Plazo", "Corto Plazo", "Ac - Otros"],
    // 'Pérdidas' context: operating expenses of the asset.
    "Comisiones":       ["Gestión", "Custodia", "Transacción", "Cm - Otros"],
    "Suministros":      ["Agua Activo", "Luz Activo", "Gas Activo", "Limpieza", "Internet", "Sum - Otros"],
    "Comunidad":        ["Cuota Comunidad", "Derramas", "Cmn - Otros"],
    "Reparaciones":     ["Reparación Menor", "Reparación Mayor", "Rep - Otros"],
    "Seguros Activo":   ["Multirriesgo", "Vida", "SegA - Otros"],
    "Impuestos Activo": ["IBI", "Plusvalía", "Tasas", "ImpA - Otros"],
};

// === TRANSFERS (bucket=TRASPASO; neutral in P&L) ===
export const TRANSFER_TAXONOMY: { [key: string]: string[] } = {
    "Movimiento Interno": ["Traspaso entre cuentas", "Traspaso en efectivo", "Aportación Cuenta Común"],
    "Externo":            ["Traspaso entre cuentas", "Traspaso en efectivo"],
};

/**
 * OFFICIAL TAXONOMY V3 — source of truth for categories and subcategories.
 * The bucket (GASTO/INGRESO/INVERSION/TRASPASO) is derived from the category via
 * getBucketFromCategory; 'Préstamo' requires opts.tipo / opts.contexto to
 * disambiguate GASTO vs INGRESO vs INVERSION.
 */
export const FINANCIAL_TAXONOMY: { [key: string]: string[] } = {
    ...EXPENSE_TAXONOMY,
    ...INCOME_TAXONOMY,
    ...INVESTMENT_TAXONOMY,
    ...TRANSFER_TAXONOMY,
};

/** Categories that belong to the GASTO bucket. */
export const EXPENSE_CATEGORIES: ReadonlySet<string> = new Set(Object.keys(EXPENSE_TAXONOMY));
/** Categories that belong to the INGRESO bucket. */
export const INCOME_CATEGORIES: ReadonlySet<string> = new Set(Object.keys(INCOME_TAXONOMY));
/** Categories that belong to the INVERSION bucket (require assetId for assets). */
export const INVESTMENT_CATEGORIES: ReadonlySet<string> = new Set(Object.keys(INVESTMENT_TAXONOMY));
/** Categories that belong to the TRASPASO bucket. */
export const TRANSFER_CATEGORIES: ReadonlySet<string> = new Set(Object.keys(TRANSFER_TAXONOMY));

/**
 * Mapping of tipo+contexto → valid categories. Used by forms and dropdowns
 * to filter the available categories based on the selected classification.
 */
export const CATEGORIES_BY_TIPO: Readonly<Record<TipoMovimiento, readonly string[]>> = {
    Saldo: ['Retribuciones', 'Venta', 'Ajustes', 'Donación', 'Herencia', 'Compra', 'Personales', 'Formación', 'Ocio', 'Transporte', 'Vivienda', 'Salud', 'Mascotas', 'Trabajo', 'Préstamo'],
    'Inversión': ['Adquisición', 'Reinversión', 'Fondos', 'Retiro', 'Dividendos', 'Alquiler Cobrado', 'Préstamo', 'Comisiones', 'Suministros', 'Comunidad', 'Reparaciones', 'Seguros Activo', 'Impuestos Activo'],
    'Traspaso': ['Movimiento Interno', 'Externo'],
};

/**
 * Reverse index category → tipo(s). Every V3 category belongs to exactly one tipo
 * EXCEPT 'Préstamo', which is trans-contextual (Saldo | Inversión).
 */
const TIPOS_BY_CATEGORY: ReadonlyMap<string, readonly TipoMovimiento[]> = (() => {
    const index = new Map<string, TipoMovimiento[]>();
    for (const [tipo, cats] of Object.entries(CATEGORIES_BY_TIPO) as [TipoMovimiento, readonly string[]][]) {
        for (const cat of cats) {
            const tipos = index.get(cat);
            if (tipos) { if (!tipos.includes(tipo)) tipos.push(tipo); }
            else index.set(cat, [tipo]);
        }
    }
    return index;
})();

/**
 * Derives the `tipo` a V3 category belongs to — the inverse of CATEGORIES_BY_TIPO.
 * This is what keeps the hierarchy coherent: an Inversión/Traspaso category can never
 * stay classified as tipo='Saldo' just because nothing set `tipo` explicitly.
 *
 * Returns null when the category is unknown (empty, legacy or user-invented): the caller
 * must keep whatever tipo it already had instead of forcing 'Saldo'.
 *
 * 'Préstamo' is ambiguous (Saldo | Inversión) and resolves via opts: an asset-linked
 * loan (or one already typed as Inversión) is Inversión; otherwise Saldo.
 */
export const getTipoFromCategory = (
    category: string | undefined | null,
    opts?: { tipo?: string; hasAsset?: boolean },
): TipoMovimiento | null => {
    if (!category) return null;
    const tipos = TIPOS_BY_CATEGORY.get(category);
    if (!tipos || tipos.length === 0) return null;
    if (tipos.length === 1) return tipos[0];
    // Ambiguous ('Préstamo'): keep the current tipo if it is one of the valid ones,
    // else fall back to Inversión when the tx is linked to an asset.
    if (opts?.tipo && tipos.includes(opts.tipo as TipoMovimiento)) return opts.tipo as TipoMovimiento;
    if (opts?.hasAsset) return 'Inversión';
    return tipos[0];
};

/**
 * Returns the fundamental bucket for a V3 category.
 * 'Préstamo' is trans-contextual: accepts opts to disambiguate the correct bucket.
 * Returns null if the category doesn't belong to the current taxonomy.
 */
export const getBucketFromCategory = (
    category: string | undefined | null,
    opts?: { tipo?: string; amount?: number },
): 'GASTO' | 'INGRESO' | 'INVERSION' | 'TRASPASO' | null => {
    if (!category) return null;
    if (TRANSFER_CATEGORIES.has(category)) return 'TRASPASO';
    // Préstamo: bucket depends on tipo
    if (category === 'Préstamo') {
        if (opts?.tipo === 'Inversión') return 'INVERSION';
        if (opts?.amount !== undefined) {
            return opts.amount > 0 ? 'INGRESO' : 'GASTO';
        }
        return 'GASTO'; // default: installment or loan to a third party
    }
    if (EXPENSE_CATEGORIES.has(category)) return 'GASTO';
    if (INCOME_CATEGORIES.has(category)) return 'INGRESO';
    if (INVESTMENT_CATEGORIES.has(category)) return 'INVERSION';
    return null;
};

/**
 * True if the category requires assetId (Inversión or Préstamo under Inversión).
 */
export const requiresAsset = (category: string | undefined | null, opts?: { tipo?: string }): boolean => {
    if (!category) return false;
    if (category === 'Préstamo' && opts?.tipo === 'Inversión') return true;
    return INVESTMENT_CATEGORIES.has(category);
};
